import { emojis as defaultEmojis, type EmojiItem } from '@tiptap/extension-emoji';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionKeyDownProps, SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import { fuzzyMatch } from '../../../common/functions/fuzzy-match';
import { EmojiSuggestionList, type EmojiSuggestionListRef } from './emoji-suggestion-list';

const MAX_RESULTS = 5;

// Regional indicator letters (`:regional_indicator_a:` etc.) sit at the top of the default
// list but are flag-construction primitives, not standalone picks. Push them to the end so
// they're still searchable but don't dominate the empty-query or substring-match results.
const isRegional = (item: EmojiItem) => item.name.startsWith('regional_indicator_');
const searchableEmojis = [...defaultEmojis.filter((e) => !isRegional(e)), ...defaultEmojis.filter(isRegional)];

// Rank candidates so the most direct shortcode hits surface first. Without this, the bare
// fuzzy-subsequence filter returned every emoji tagged with the query in array order.
const scoreEmoji = (item: EmojiItem, query: string): number => {
  const lowerTags = item.tags.map((t) => t.toLowerCase());
  if (item.shortcodes.some((s) => s === query)) return 100;
  if (item.shortcodes.some((s) => s.startsWith(query))) return 80;
  if (item.shortcodes.some((s) => s.includes(query))) return 60;
  if (lowerTags.some((t) => t === query)) return 40;
  if (lowerTags.some((t) => t.includes(query))) return 20;
  if (item.shortcodes.some((s) => fuzzyMatch(s, query)) || lowerTags.some((t) => fuzzyMatch(t, query))) return 10;
  return 0;
};

const filterEmojis = (query: string): EmojiItem[] => {
  if (!query) {
    return searchableEmojis.slice(0, MAX_RESULTS);
  }
  const q = query.toLowerCase();
  return searchableEmojis
    .map((item) => ({ item, score: scoreEmoji(item, q) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_RESULTS)
    .map(({ item }) => item);
};

export const buildEmojiSuggestion = (): Partial<SuggestionOptions<EmojiItem>> => ({
  char: ':',
  // The CSS rule for `.emoji-suggestion-trigger` gives this decoration `anchor-name`,
  // which the popover binds to via `position-anchor`. Browser-driven positioning then
  // follows the trigger on scroll/resize without JS.
  decorationClass: 'emoji-suggestion-trigger',
  items: ({ query }) => filterEmojis(query),
  render: () => {
    let renderer: ReactRenderer<EmojiSuggestionListRef> | undefined;
    let popup: HTMLDivElement | undefined;

    const mount = (props: SuggestionProps<EmojiItem>) => {
      popup = document.createElement('div');
      popup.dataset.slot = 'emoji-suggestion';
      popup.popover = 'manual';
      document.body.appendChild(popup);
      renderer = new ReactRenderer(EmojiSuggestionList, {
        editor: props.editor,
        props,
      });
      popup.appendChild(renderer.element);
      popup.showPopover();
    };

    const unmount = () => {
      popup?.remove();
      popup = undefined;
      renderer?.destroy();
      renderer = undefined;
    };

    return {
      onStart: (props: SuggestionProps<EmojiItem>) => {
        if (props.items.length === 0) {
          return;
        }
        mount(props);
      },
      onUpdate: (props: SuggestionProps<EmojiItem>) => {
        if (props.items.length === 0) {
          unmount();
          return;
        }
        if (!renderer) {
          mount(props);
          return;
        }
        renderer.updateProps(props);
      },
      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (props.event.key === 'Escape') {
          unmount();
          return true;
        }
        const handled = renderer?.ref?.onKeyDown(props) ?? false;
        if (handled) {
          return true;
        }
        // Swallow nav keys even when the popup isn't mounted (empty results) so that typing
        // `:zzz<Tab>` doesn't insert a tab character into the document mid-session.
        const NAV_KEYS = ['ArrowUp', 'ArrowDown', 'Enter', 'Tab'];
        return NAV_KEYS.includes(props.event.key);
      },
      onExit: () => {
        unmount();
      },
    };
  },
});
